import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import WebCountryFlag, { ReactCountryFlag } from "react-country-flag";
import { mapiLoadCountries } from "../../../../../services/mapi.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { capturePosthogEvent } from "../../../../../utils/analytics";
countries.registerLocale(enLocale);

type MapiCountry = {
  iso3Code: string;
  iso2Code: string;
  name: string;
  baseCurrency: string;
  sendActive: boolean;
  receiveActive: boolean;
  sendCurrencies: string[];
  receiveCurrencies: string[];
};

type MapiChooseCountryStepProps = {
  onCountrySelected?: (code: string) => void;
  testID?: string | undefined;
};

export default function MapiChooseCountryStep({
  onCountrySelected,
  testID,
}: MapiChooseCountryStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countriesData, setCountriesData] = useState<MapiCountry[]>([]);

  const setSelectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryCode
  );
  const setSelectedDestinationCountryName = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryName
  );
  const setMapiCountriesList = useSessionStore(
    (s: SessionState) => (s as any).setMapiCountriesList
  );

  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper]
      .map((c) => String.fromCodePoint(base + c.charCodeAt(0)))
      .join("");
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await mapiLoadCountries();
        if (result.success && Array.isArray(result.data)) {
          setMapiCountriesList?.(result.data);
          const active = result.data.filter(
            (c: MapiCountry) => c.receiveActive && !!c.name && !!c.iso3Code
          );
          setCountriesData(active);
        }
      } catch (e) {
        console.error("Failed to load countries in MapiChooseCountryStep", e);
      }
    };
    fetchCountries();
  }, []);

  const filteredCountries: MapiCountry[] = useMemo(() => {
    const q = search.toLowerCase();
    return countriesData.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
    );
  }, [countriesData, search]);

  const onSelectCountry = (item: MapiCountry) => {
    setSelectedCountry(item.name);
    setSelectedDestinationCountryName(item.name);
    setSelectedDestinationCountryCode(item.iso3Code);
    setTimeout(() => {
      onCountrySelected?.(item.iso3Code);
    }, 200);
    capturePosthogEvent("Mapi Send Flow - Country Capture Step", {
      "data-mg-country": item.name,
      "data-mg-country-code": item.iso3Code,
    });
  };

  return (
    <View style={styles.container} testID={testID}>
      <FlatListDataComponent
        title={t("COUNTRY_STEP_TITLE")}
        subTitle={t("COUNTRY_STEP_SUBTITLE")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredCountries}
        searchTestID="mapiCountrySearchInput"
        flatListTestID="mapiCountryList"
        renderItem={(item: MapiCountry) => {
          const alpha2 = item.iso2Code?.toUpperCase() || "";
          return (
            <TouchableOpacity
              style={styles.countryRow}
              onPress={() => onSelectCountry(item)}
              testID="mapiCountrySelected"
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {Platform.OS === "web" ? (
                  <View
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 50,
                      overflow: "hidden",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 10,
                    }}
                  >
                    {alpha2 ? (
                      <ReactCountryFlag
                        countryCode={alpha2}
                        svg
                        style={{
                          width: 35,
                          height: 35,
                          borderRadius: 50,
                        }}
                        aria-label={item.name}
                      />
                    ) : (
                      <Text style={{ fontSize: 20 }}>🌍</Text>
                    )}
                  </View>
                ) : (
                  <Text style={styles.flag}>
                    {countryCodeToEmoji(alpha2) || "🌍"}
                  </Text>
                )}
                <Text style={styles.countryName}>{item.name}</Text>
              </View>
              <View style={styles.radioButton}>
                {selectedCountry === item.name && (
                  <RadioSelectedIcon width={20} height={20} />
                )}
              </View>
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: "space-between",
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.radioButtonFilled,
  },
});
